import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CalendarDays, Download, MapPin, Users, HardHat, CalendarRange, FileText, ChevronRight } from "lucide-react";
import { StatTile, StatStrip } from "@/components/StatTile";
import { toast } from "sonner";

function istToday() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

const Dot = ({ ok, title }) => (
  <span
    className="inline-block h-3 w-3 rounded-full ring-2 ring-white"
    style={{ backgroundColor: ok ? "#10b981" : "#ef4444" }}
    title={title}
  />
);

const OPERATIONS_ITEMS = [
  {
    title: "Contractor Parks Visit Today",
    path: "/contractor-admin/dashboard",
    icon: MapPin,
    color: "bg-blue-500",
    desc: "Park visits & supervisor attendance tracking",
  },
  {
    title: "Contractor Work Captures by Contractor Supervisor",
    path: "/contractor-admin/work-captures",
    icon: HardHat,
    color: "bg-amber-500",
    desc: "Work activity captures & photo logs",
  },
  {
    title: "Workers at Work",
    path: "/contractor-admin/workers-at-work",
    icon: Users,
    color: "bg-emerald-500",
    desc: "Active field manpower & worker headcounts",
  },
  {
    title: "Attendance Calendar",
    path: "/contractor-admin/attendance-calendar",
    icon: CalendarRange,
    color: "bg-purple-500",
    desc: "Monthly attendance history & matrix",
  },
  {
    title: "Monthly Feedback",
    path: "/contractor-admin/feedback",
    icon: FileText,
    color: "bg-rose-500",
    desc: "Feedback entries & supervisor performance reviews",
  },
];

export default function ContractorAdminDashboardPage() {
  const [date, setDate] = useState(istToday());
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ visited: 0, missed: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/dashboard/park-visits", {
        params: { role: "contractor_supervisor", date },
      });
      setRows(data.rows || []);
      setCounts(data.counts || { visited: 0, missed: 0, total: 0 });
    } catch {
      toast.error("Failed to load park visits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [date]);

  const exportXlsx = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/dashboard/park-visits?role=contractor_supervisor&date=${date}&download=1`,
        { headers: { Authorization: `Bearer ${tokenStore.get()}` } },
      );
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `park_visits_contractor_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <AppShell
      title="Operations Dashboard"
      subtitle="Contractor Parks Visit Today & Operations Overview"
      action={
        <Button onClick={exportXlsx} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="contractor-admin-export-xlsx">
          <Download className="mr-1.5 h-4 w-4" /> Download Excel
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Operations Quick Nav Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {OPERATIONS_ITEMS.map((item) => {
            const Icon = item.icon;
            const isCurrent = item.path === "/contractor-admin/dashboard";
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col justify-between p-3.5 rounded-xl border transition-all ${
                  isCurrent
                    ? "bg-slate-900 text-white border-slate-800 shadow-md ring-2 ring-amber-400/40"
                    : "bg-card hover:bg-accent/50 border-border text-card-foreground hover:shadow-sm"
                }`}
                data-testid={`ops-nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-2 rounded-lg text-white ${item.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <ChevronRight className={`h-4 w-4 ${isCurrent ? "text-amber-400" : "text-muted-foreground"}`} />
                  </div>
                  <h3 className="text-xs font-semibold leading-snug line-clamp-2">
                    {item.title}
                  </h3>
                </div>
                <p className={`text-[11px] mt-2 line-clamp-2 ${isCurrent ? "text-slate-400" : "text-muted-foreground"}`}>
                  {item.desc}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Section Header */}
        <div className="border-t pt-4">
          <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-emerald-600" />
            Contractor Parks Visit Today
          </h2>
          
          <div className="flex flex-col gap-4">
            <Card>
              <CardContent className="flex flex-col gap-1.5 p-4 sm:max-w-xs">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <CalendarDays className="mr-1 inline h-3 w-3" /> Date
                </Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="contractor-admin-date-input" className="h-10" />
              </CardContent>
            </Card>

            <StatStrip cols={3}>
              <StatTile icon={MapPin} accent="slate" label="Total Parks" value={counts.total} testid="contractor-admin-total" />
              <StatTile icon={MapPin} accent="emerald" label="Visited" value={counts.visited} testid="contractor-admin-visited" />
              <StatTile icon={MapPin} accent="rose" label="Not Visited" value={counts.missed} testid="contractor-admin-missed" />
            </StatStrip>

            {loading ? (
              <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
            ) : rows.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No parks found.</CardContent></Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="contractor-admin-visits-rows">
                    <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                      <tr className="border-b border-slate-200">
                        <th className="whitespace-nowrap px-2 py-2 text-center">#</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left">Park Code</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left">Park Name</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center">Visited</th>
                        <th className="whitespace-nowrap px-3 py-2 text-center">Attendance</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left">Assigned Supervisor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.park_id} className="border-b border-slate-100" data-testid={`contractor-admin-row-${r.park_code}`}>
                          <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs text-slate-500 tabular-nums">{i + 1}</td>
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs font-semibold text-slate-800">{r.park_code}</td>
                          <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">{r.park_name}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-center">
                            <div className="flex justify-center">
                              <Dot ok={!!r.visited} title={r.visited ? "Visited" : "Not visited"} />
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-center">
                            <div className="flex justify-center">
                              <Dot ok={!!r.attendance_done} title={r.attendance_done ? "Attendance marked" : "Attendance NOT marked"} />
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-left">
                            {(r.assigned || []).length === 0 ? (
                              <span className="text-slate-400">— No supervisor assigned</span>
                            ) : (
                              <div className="flex flex-col gap-0.5">
                                {r.assigned.map((u, idx) => (
                                  <span key={idx} className="text-[12px]">
                                    <span className="font-medium">{u.name}</span>
                                    <span className="ml-1.5 text-[10px] text-slate-400">({u.username})</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
                  <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{rows.length} park{rows.length === 1 ? "" : "s"}</span>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

